import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Pencil, Check, X } from 'lucide-react';
import type { CoaAccount } from './CoaPreview';
import { TYPE_ORDER, TYPE_LABELS, TYPE_COLORS, isParentCode, getDepthBgClass } from '../lib/coa-display';
import { tr } from '../lib/i18nHelpers';

interface CoaResultTreeProps {
  accounts: CoaAccount[];
  balances: Record<string, number>;
  /** When provided, each row gets a name pencil; receives the row's current code + new name. */
  onEditName?: (code: string, name: string) => void;
  /** When provided, leaf rows get a code pencil. Returns an error message, or null on success. */
  onEditCode?: (oldCode: string, newCode: string) => string | null;
}

const fmt = (n: number) => n.toLocaleString('en-US');

/** Sum of descendant *leaf* balances (parents recurse, their own balance is not counted). */
function subtreeLeafTotal(
  code: string,
  accounts: CoaAccount[],
  balances: Record<string, number>,
): number {
  let sum = 0;
  const queue = [code];
  while (queue.length) {
    const cur = queue.shift()!;
    for (const a of accounts) {
      if (a.parent_code !== cur) continue;
      if (isParentCode(a.account_code, accounts)) queue.push(a.account_code);
      else sum += balances[a.account_code] || 0;
    }
  }
  return sum;
}

interface EditState {
  code: string;
  field: 'name' | 'code';
  value: string;
}

export default function CoaResultTree({ accounts, balances, onEditName, onEditCode }: CoaResultTreeProps) {
  const [collapsedTypes, setCollapsedTypes] = useState<Record<string, boolean>>({});
  const [collapsedAccounts, setCollapsedAccounts] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<EditState | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(TYPE_ORDER.map(t => [t, [] as CoaAccount[]])) as Record<string, CoaAccount[]>;
    for (const a of accounts) {
      const t = a.account_type || 'expense';
      if (!g[t]) g[t] = [];
      g[t].push(a);
    }
    return g;
  }, [accounts]);

  const topLevelFor = (type: string) => {
    // Scope to the same group: an account is a root here when it has no parent
    // OR its parent is absent from this group. This keeps every account rendered
    // exactly once even when a child's parent belongs to a different account_type.
    const groupCodes = new Set(grouped[type].map(a => a.account_code));
    return grouped[type]
      .filter(a => !a.parent_code || !groupCodes.has(a.parent_code))
      .sort((a, b) => a.account_code.localeCompare(b.account_code));
  };

  const commitName = (code: string, value: string) => {
    const name = value.trim();
    if (!name) { setEditError(tr('Name required', '請輸入名稱', '请输入名称')); return; }
    onEditName?.(code, name);
    setEditing(null);
    setEditError(null);
  };

  const commitCode = (oldCode: string, value: string) => {
    const next = value.trim();
    if (next === oldCode) { setEditing(null); setEditError(null); return; }
    const err = onEditCode?.(oldCode, next) ?? null;
    if (err) { setEditError(err); return; }
    setEditing(null);
    setEditError(null);
  };

  const renderNode = (account: CoaAccount, type: string, depth: number): React.ReactNode => {
    const code = account.account_code;
    const isParent = isParentCode(code, accounts);
    const children = grouped[type]
      .filter(c => c.parent_code === code)
      .sort((a, b) => a.account_code.localeCompare(b.account_code));
    const isExpanded = !collapsedAccounts[code];
    const total = isParent ? subtreeLeafTotal(code, accounts, balances) : 0;
    const raw = balances[code];
    const value = isParent ? (total ? fmt(total) : '') : (raw ? fmt(raw) : '');

    const editingName = editing?.code === code && editing.field === 'name';
    const editingCode = editing?.code === code && editing.field === 'code';
    const canEditName = !!onEditName;
    const canEditCode = !!onEditCode && !isParent;

    return (
      <React.Fragment key={code}>
        <div
          data-testid={isParent ? `coa-result-rollup-${code}` : `coa-result-leaf-${code}`}
          onClick={() => {
            if (isParent) setCollapsedAccounts(prev => ({ ...prev, [code]: !prev[code] }));
          }}
          style={{ paddingLeft: `${12 + depth * 16}px` }}
          className={`flex items-center gap-2 py-1.5 pr-3 border-b last:border-b-0 select-none ${
            getDepthBgClass(code, accounts)
          } ${isParent && children.length ? 'cursor-pointer' : ''}`}
        >
          <span className="w-4 flex-shrink-0">
            {isParent && children.length > 0 ? (
              isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
            ) : (
              <span className="w-3" />
            )}
          </span>

          {editingCode ? (
            <span className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              <input
                data-testid={`coa-result-code-input-${code}`}
                autoFocus
                value={editing!.value}
                onChange={e => setEditing({ code, field: 'code', value: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitCode(code, editing!.value);
                  if (e.key === 'Escape') { setEditing(null); setEditError(null); }
                }}
                className="w-20 rounded border px-1 py-0.5 font-mono text-[11px]"
                maxLength={5}
              />
              <button
                type="button"
                data-testid={`coa-result-code-save-${code}`}
                onClick={() => commitCode(code, editing!.value)}
                className="rounded bg-primary p-0.5 text-primary-foreground"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => { setEditing(null); setEditError(null); }}
                className="rounded border p-0.5 text-muted-foreground hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <span className="flex items-center gap-1 flex-shrink-0">
              <code className={`font-mono text-[11px] ${isParent ? 'font-bold' : ''}`}>{code}</code>
              {canEditCode && (
                <button
                  type="button"
                  data-testid={`coa-result-code-edit-${code}`}
                  title={tr('Edit code', '編輯編號', '编辑编号')}
                  onClick={e => { e.stopPropagation(); setEditError(null); setEditing({ code, field: 'code', value: code }); }}
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </span>
          )}

          {editingName ? (
            <span className="flex min-w-0 flex-1 items-center gap-1" onClick={e => e.stopPropagation()}>
              <input
                data-testid={`coa-result-name-input-${code}`}
                autoFocus
                value={editing!.value}
                onChange={e => setEditing({ code, field: 'name', value: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter') commitName(code, editing!.value);
                  if (e.key === 'Escape') { setEditing(null); setEditError(null); }
                }}
                className="min-w-0 flex-1 rounded border px-1.5 py-0.5 text-[11px]"
              />
              <button
                type="button"
                data-testid={`coa-result-name-save-${code}`}
                onClick={() => commitName(code, editing!.value)}
                className="rounded bg-primary p-0.5 text-primary-foreground"
              >
                <Check className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => { setEditing(null); setEditError(null); }}
                className="rounded border p-0.5 text-muted-foreground hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <span className={`min-w-0 flex-1 truncate ${isParent ? 'font-semibold' : 'text-muted-foreground'}`}>
              {account.account_name}
              {canEditName && (
                <button
                  type="button"
                  data-testid={`coa-result-name-edit-${code}`}
                  title={tr('Edit name', '編輯名稱', '编辑名称')}
                  onClick={e => { e.stopPropagation(); setEditError(null); setEditing({ code, field: 'name', value: account.account_name }); }}
                  className="ml-1 inline-flex rounded p-0.5 align-middle text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              )}
            </span>
          )}

          <span
            data-testid={`coa-result-value-${code}`}
            className={`w-24 flex-shrink-0 text-right text-[11px] tabular-nums ${
              isParent ? 'text-muted-foreground' : 'font-mono'
            }`}
          >
            {value}
          </span>
        </div>

        {editing?.code === code && editError && (
          <div
            data-testid={`coa-result-code-error-${code}`}
            style={{ paddingLeft: `${12 + depth * 16}px` }}
            className="pr-3 pb-1.5 text-[10px] text-destructive"
          >
            {editError}
          </div>
        )}

        {isParent && isExpanded && children.map(c => renderNode(c, type, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div data-testid="coa-result-tree" className="border rounded-lg overflow-hidden text-xs">
      {TYPE_ORDER.filter(t => grouped[t]?.length > 0).map(type => {
        const isExpanded = !collapsedTypes[type];
        return (
          <div key={type} className="border-b last:border-b-0">
            <button
              type="button"
              onClick={() => setCollapsedTypes(prev => ({ ...prev, [type]: !prev[type] }))}
              className="w-full flex items-center gap-2 px-3 py-2 bg-muted/20 hover:bg-muted/40 transition-colors text-left"
            >
              {isExpanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-semibold ${TYPE_COLORS[type]}`}>
                {TYPE_LABELS[type]}
              </span>
              <span className="text-xs text-muted-foreground">
                {grouped[type].length} {tr('accounts', '個科目', '个科目')}
              </span>
            </button>

            {isExpanded && (
              <div>
                {topLevelFor(type).map(a => renderNode(a, type, 0))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
